import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronLeft, Filter, Info } from "lucide-react";
import WorldMap from "@/components/WorldMap";
import FiltersPanel from "@/components/FiltersPanel";
import SearchBar from "@/components/SearchBar";
import LocationPanel from "@/components/LocationPanel";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLang } from "@/lib/lang";
import { API } from "@/lib/auth";

export default function MapPage() {
  const { t, lang, setLang } = useLang();
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [active, setActive] = useState([]);
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => {
      setCategories(r.data);
      setActive(r.data.map((c) => c.key));
    });
    axios.get(`${API}/locations`).then((r) => setLocations(r.data));
  }, []);

  const visible = useMemo(
    () => locations.filter((l) => active.includes(l.category)),
    [locations, active]
  );

  const searchResults = useMemo(() => {
    if (!q.trim()) return [];
    const s = q.toLowerCase();
    return locations.filter((l) => {
      const n = ((l.name?.fr || "") + " " + (l.name?.en || "") + " " + (l.country || "")).toLowerCase();
      return n.includes(s);
    });
  }, [q, locations]);

  const toggle = (k) => setActive((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  const allOn = () => setActive(categories.map((c) => c.key));
  const noneOn = () => setActive([]);

  const handlePick = (loc) => {
    setSelected(loc);
    setFocus({ lat: loc.lat, lng: loc.lng });
    setQ("");
  };

  return (
    <div className="fixed inset-0 bg-white">
      <WorldMap locations={visible} onSelect={setSelected} focus={focus} />

      {/* Top left: back + lang + profile */}
      <div className="absolute top-4 left-4 z-[1000] flex items-center gap-2">
        <Link to="/">
          <Button
            size="icon"
            variant="outline"
            data-testid="back-home-btn"
            className="glass border-zinc-200 hover:bg-zinc-50 rounded-full h-11 w-11 text-zinc-900"
          >
            <ChevronLeft size={18} />
          </Button>
        </Link>
        <div className="glass rounded-full px-1 py-1 flex items-center gap-1">
          {["fr", "en"].map((code) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              data-testid={`map-lang-${code}-btn`}
              className={`px-3 py-1 rounded-full text-xs font-mono-data uppercase tracking-wider transition-colors ${
                lang === code ? "bg-[#8b6f3d] text-white" : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {code}
            </button>
          ))}
        </div>
        <Link to="/profile">
          <Button
            variant="outline"
            data-testid="profile-nav-btn"
            className="glass border-zinc-200 hover:bg-zinc-50 rounded-full h-11 text-zinc-900 gap-2 px-4"
          >
            <span className="text-base">🧭</span>
            <span className="hidden sm:inline text-xs font-mono-data uppercase tracking-wider">{t("profileBtn")}</span>
          </Button>
        </Link>
      </div>

      {/* Top center: search */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] w-[92%] max-w-lg">
        <SearchBar value={q} onChange={setQ} results={searchResults} onPick={handlePick} />
      </div>

      {/* Left: filters (desktop) */}
      <div className="absolute bottom-6 left-4 z-[1000] hidden md:block">
        <FiltersPanel
          categories={categories}
          active={active}
          onToggle={toggle}
          onAll={allOn}
          onNone={noneOn}
        />
      </div>

      {/* Mobile filters */}
      <div className="absolute bottom-6 left-4 z-[1000] md:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button
              data-testid="mobile-filter-btn"
              className="glass border border-zinc-200 bg-white/90 text-zinc-900 rounded-full h-12 px-5 hover:bg-zinc-50"
            >
              <Filter size={16} className="mr-2" /> {t("filters")}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-white border-r border-zinc-200 p-4">
            <FiltersPanel
              categories={categories}
              active={active}
              onToggle={toggle}
              onAll={allOn}
              onNone={noneOn}
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* Bottom right counter */}
      <div className="absolute bottom-6 right-4 z-[1000] glass rounded-full px-4 py-2 font-mono-data text-[11px] text-zinc-700 flex items-center gap-2" data-testid="count-badge">
        <Info size={12} className="text-[#8b6f3d]" />
        {visible.length} / {locations.length}
      </div>

      {selected && <LocationPanel location={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
